import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import React from 'react';
import { Box } from '../box';
import { TypographySettings } from '../config';
import { ColorPalette } from '../config/color-palette';
import { Tooltip } from '../tooltip';
import { FloatingActionButtonMenu, IFloatingActionButtonMenuItemProps } from './floating-action-button-menu';

export interface IFloatingActionButtonProps {
  buttonText?: string | React.ReactNode;
  icon?: IconDefinition;
  textIcon?: string | React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  testId?: string;
  tooltipText?: string;
  badgeContent?: number;
  iconColor?: string;
  loading?: boolean;
  inverted?: boolean;
  menuItems?: IFloatingActionButtonMenuItemProps[];
  'data-testid'?: string;
  disabledTooltipText?: string;
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseOver?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void;
  'data-vanguard'?: string;
  ariaLabel?: string;
}

interface IFabStyleParams {
  iconColor?: string;
  loading?: boolean;
}

const useStyles = makeStyles<IFabStyleParams, 'disabled' | 'icon' | 'iconInverted' | 'iconDisabled'>()(
  (theme: Theme, params, classes) => ({
    button: {
      alignItems: 'center',
      color: ColorPalette.DefaultTextColor,
      display: 'flex',
      flexDirection: 'column',
      fontSize: 10,
      lineHeight: 1.2,
      minWidth: 0,
      padding: 0,
      position: 'relative',
      textTransform: 'none',
      width: '100%',
      wordBreak: 'break-all',
      [`.${classes.disabled} &`]: {
        color: 'rgba(0, 0, 0, .46)',
      },
      opacity: params.loading ? 0.8 : 1,
      pointerEvents: params.loading ? 'none' : 'auto',
      '&:hover': {
        backgroundColor: 'transparent',
        [`& .${classes.icon}`]: {
          backgroundColor: '#204758',
        },
        [`& .${classes.iconInverted}`]: {
          backgroundColor: 'rgba(0, 0, 0, .07)',
          color: 'currentColor',
          borderColor: 'transparent',
        },
      },
      '&:focus': {},
    },
    icon: {
      alignItems: 'center',
      backgroundColor: ColorPalette.CityNightSkiesDark,
      borderRadius: '50%',
      color: ColorPalette.WHITE,
      display: 'flex',
      flex: '1 0 auto',
      fontSize: '1.25rem',
      height: 40,
      justifyContent: 'center',
      position: 'relative',
      width: 40,
      transition: 'all .3s linear',
      'button:focus > &': {
        backgroundColor: '#6ba5bf',
      },
      [`&.${classes.iconDisabled}`]: {
        backgroundColor: 'rgba(0,0,0,.12)',
        cursor: 'default',
        pointerEvents: 'none',
      },
      [`&.${classes.iconInverted}`]: {
        backgroundColor: 'transparent',
        border: `1px solid ${ColorPalette.CityNightSkiesDark}`,
        color: ColorPalette.CityNightSkiesDark,
        [`&.${classes.iconDisabled}`]: {
          color: '#d2d2d2',
          borderColor: '#d2d2d2',
        },
        'button:focus > &': {
          backgroundColor: '#e0ecf1',
        },
      },
      'button:hover > &': {
        boxShadow: 'rgb(0 0 0 / 20%) 0px 3px 5px -1px, rgb(0 0 0 / 14%) 0px 6px 10px 0px, rgb(0 0 0 / 12%) 0px 1px 18px 0px',
      },
      'button:active > &': {
        boxShadow: 'rgb(0 0 0 / 20%) 0px 7px 8px -4px, rgb(0 0 0 / 14%) 0px 12px 17px 2px, rgb(0 0 0 / 12%) 0px 5px 22px 4px',
      },
    },
    iconInverted: {},
    iconDisabled: {},
    item: {
      marginBottom: theme.spacing(2),
      position: 'relative',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    label: {
      display: 'flex',
    },
    disabled: {},
    badge: {
      position: 'absolute',
      right: -2,
      top: -5,
      color: theme.palette.common.white,
      backgroundColor: ColorPalette.Crimson,
      display: 'flex',
      fontSize: 11,
      borderRadius: 20,
      minWidth: 17,
      height: 17,
      padding: '0 5px',
      fontFamily: TypographySettings.AlternativeFontFamily,
      fontWeight: 500,
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 2,
    },
    loadingButton: {
      position: 'absolute',
      color: ColorPalette.CityNightSkiesDark,
      transition: 'opacity .2s ease',
      zIndex: 1,
      opacity: params.loading ? 1 : 0,
    },
    loadingButtonBase: {
      zIndex: 0,
      color: ColorPalette.WHITE,
      opacity: params.loading ? 0.8 : 0,
    },
    textUnder: {
      wordBreak: 'keep-all',
      marginTop: theme.spacing(0.5),
    },
  })
);

export function FloatingActionButton(props: IFloatingActionButtonProps) {
  const {
    onClick,
    icon,
    buttonText = '',
    disabled = false,
    tooltipText = '',
    badgeContent = 0,
    iconColor,
    loading = false,
    inverted = false,
    'data-testid': testid,
    menuItems,
    textIcon = '',
    disabledTooltipText = 'Denne knap er deaktiveret i øjeblikket',
    onMouseEnter,
    onMouseOver,
    onMouseLeave,
    'data-vanguard': vanguard,
    ariaLabel,
  } = props;
  const { classes } = useStyles({ iconColor, loading });
  const dataTestAttrib = { 'data-testid': testid };
  const [submenuAnchor, setSubmenuAnchor] = React.useState<null | HTMLElement>(null);
  const buttonSubmenu = menuItems ? <FloatingActionButtonMenu menuItems={menuItems} anchorEl={submenuAnchor} handleClose={() => setSubmenuAnchor(null)} /> : null;

  const onClickWrapper = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (onClick) {
      onClick();
    }

    if (menuItems) {
      setSubmenuAnchor(event.currentTarget);
    }
  };

  return (
    <div className={classes.item} onMouseEnter={onMouseEnter} onMouseOver={onMouseOver} onMouseLeave={onMouseLeave} {...dataTestAttrib}>
      <Tooltip title={disabled ? disabledTooltipText : tooltipText} placement="left-start">
        <span>
          <Button
            onClick={onClickWrapper}
            disabled={disabled}
            classes={{ root: classes.button, disabled: classes.disabled }}
            disableRipple
            data-vanguard={vanguard}
            aria-label={ariaLabel}
          >
            <div className={clsx(classes.icon, { [classes.iconDisabled]: disabled, [classes.iconInverted]: inverted })}>
              {!!badgeContent && <span className={classes.badge}>{badgeContent < 100 ? badgeContent : '+99'}</span>}
              {icon ? <FontAwesomeIcon icon={icon} size="1x" /> : textIcon}
              {loading && (
                <>
                  <CircularProgress className={classes.loadingButton} size={50} thickness={3} disableShrink />
                  <CircularProgress className={clsx(classes.loadingButton, classes.loadingButtonBase)} size={50} thickness={3} variant="determinate" value={100} />
                </>
              )}
            </div>
            <Box component="span" className={clsx(classes.textUnder)}>
              {buttonText}
            </Box>
          </Button>
          {buttonSubmenu}
        </span>
      </Tooltip>
    </div>
  );
}
